import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CompaniesApi, CompanyInListDto } from '@buspro/web/apis';
import { AppService, LocalAuthService } from '@buspro/web/core';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'buspro-admin-business-switcher',
  standalone: true,
  imports: [NzSelectComponent, NzOptionComponent, MatIconModule, FormsModule, AsyncPipe],
  template: `
    <nz-select
      class="w-96 bg-gray-200 rounded-lg"
      nzSize="large"
      [nzShowSearch]="true"
      [nzBorderless]="true"
      [ngModel]="selected"
      (ngModelChange)="onBusinessContextSwitch($event)"
      [nzLoading]="query.isLoading()"
      [nzPlaceHolder]="placeholderTpl"
      nzNotFoundContent="No Business Found"
      [nzCustomTemplate]="selectedDisplayTpl"
      [nzBackdrop]="true"
    >
      <ng-template #placeholderTpl>
        <span class="font-normal text-gray-400">Switch Business Context</span>
      </ng-template>
      <ng-template #selectedDisplayTpl let-selected>
        <div class="flex items-center w-full space-x-2">
          <mat-icon>business</mat-icon>
          <div class="font-bold">{{ selected.nzLabel }}</div>
        </div>
      </ng-template>

      @if (query.isSuccess()) { @for (tenant of tenants$|async; track tenant.id)
      {
      <nz-option [nzLabel]="tenant.name" [nzValue]="tenant"></nz-option>
      } }
    </nz-select>
  `,
  styles: ``,
})
export class BusinessSwitcherComponent {
  router = inject(Router);
  appService = inject(AppService);
  authService = inject(LocalAuthService);
  private companiesApi = inject(CompaniesApi);
  private queryClient = injectQueryClient();
  tenants$ = new BehaviorSubject<CompanyInListDto[]>([]);
  snackBar = inject(MatSnackBar);

  query = injectQuery(() => ({
    queryKey: ['tenants'],
    queryFn: async () => {
      const data = await this.companiesApi.companyControllerGetCompanies().toPromise();
      return data || [];
    },
    select: (data: CompanyInListDto[]) => {
      // this.appService.currentTenant$.set(data.length ? data[0] : null)
      // this.selected = data.length ? data[0] : null
      this.tenants$.next(data);
    },
  }));

  selected = this.appService.currentTenant$();

  async onBusinessContextSwitch(nextTenant: CompanyInListDto) {
    const currentTenantId = this.getTenantIdfromCurrentUrl(this.router.url);

    if (currentTenantId === nextTenant.id) {
      this.snackBar.open('You currently in the selected tenant profile', 'OKAY');
      return;
    }

    // Map CompanyInListDto to TenantInListDto format
    const tenantData: any = {
      ...nextTenant,
      address: '', // Admin doesn't require address
      createdAt: typeof nextTenant.createdAt === 'string' ? new Date(nextTenant.createdAt) : new Date(),
    };

    // Set current tenant with validation feedback
    this.appService.setCurrentTenant(tenantData).subscribe({
      next: (success) => {
        if (success) {
          // Clear all cached query data before navigating so the new tenant
          // context starts with a clean slate — no stale data from the
          // previous tenant can flash in. The route change triggers the
          // TenantDetailsResolver (different :tid URL) and each feature
          // module re-fetches with the updated tenant headers automatically.
          this.queryClient.clear();

          this.router.navigateByUrl('/tenants/context/' + nextTenant.id + '/overview');
        } else {
          this.snackBar.open('❌ Failed to switch to selected tenant. Please try again.', 'OKAY', {
            duration: 5000,
          });
        }
      },
      error: (error) => {
        this.snackBar.open('❌ Error switching tenant. Please try again.', 'OKAY', {
          duration: 5000,
        });
        console.error('Error switching tenant:', error);
      },
    });
  }

  getLastPartOfUrl(url: string): string {
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  getTenantIdfromCurrentUrl(url: string): string {
    const parts = url.split('/');
    return parts[parts.length - 2];
  }
}
